import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Theme, useMediaQuery } from '@material-ui/core';

import useGoToCheckout from 'src/hooks/use-go-to-checkout';
import useUI from 'src/hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import { useDutchiePay } from 'src/payments/hooks';
import usePaths from 'src/hooks/use-paths';

import { Button } from 'src/components/core';
import { PaySignUpButton } from 'src/payments/components/pay-sign-up-button/pay-sign-up-button';
import { WhatIsPayByBank } from 'src/payments/components/what-is-pbb';

export const CartFooter = (): JSX.Element => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down(`xs`));
  const { closeCartDrawer, variant } = useUI();
  const { taxInclusionNote } = useCart();
  const { isDutchiePayEnabledForDispo, isEnrolledDutchiePay } = useDutchiePay();
  const displayWhatIsPayByBank = isDutchiePayEnabledForDispo && !isEnrolledDutchiePay;
  const { href: checkoutHref } = usePaths({ checkout: true });
  const goToCheckout = useGoToCheckout(`in-cart`);
  const theme = useTheme();

  const proceedToCheckout = (): void => {
    closeCartDrawer();
    goToCheckout();
  };

  const buttonOverrides = isDutchiePayEnabledForDispo
    ? css`
        /* I hate these importants but...this PaySignUpButton is doing some things */
        font-family: inherit;
        font-size: 13px !important;
        font-weight: bold;
        height: auto;
        line-height: ${20 / 13};
        padding: 11px 8px !important;
        text-transform: ${theme.customized.textTransforms.buttons};
      `
    : css`
        font-size: 14px;
        line-height: ${20 / 14};
        padding: 16px 8px;
      `;

  const payByBankButtonProps = isEnrolledDutchiePay
    ? { goToCheckout }
    : { refHref: checkoutHref, enrollmentSource: `cart-rail-${String(variant)}` };

  return (
    <Footer $isMobile={isMobile}>
      {isDutchiePayEnabledForDispo && (
        <PaySignUpButton
          location='cart-rail'
          includeSubtext={false}
          buttonOverrides={buttonOverrides}
          onSignUpButtonClick={closeCartDrawer}
          {...payByBankButtonProps}
        />
      )}

      <CheckoutButton
        variant='primary'
        fluid
        onClick={proceedToCheckout}
        $buttonOverrides={buttonOverrides}
        data-testid='cart-checkout-button'
      >
        Proceed to checkout
      </CheckoutButton>

      {(displayWhatIsPayByBank || taxInclusionNote) && (
        <Notes>
          {taxInclusionNote && <div>{taxInclusionNote}</div>}
          {displayWhatIsPayByBank && <StyledWhatIsPayByBank />}
        </Notes>
      )}
    </Footer>
  );
};

const Footer = styled.footer`
  border-top: 1px solid #f5f6f7;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 16px 20px;
  width: 100%;
`;

const CheckoutButton = styled(Button)`
  ${({ $buttonOverrides }) => $buttonOverrides};
`;

const Notes = styled.div`
  color: #485055;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  gap: 2px;
  line-height: ${16 / 12};
  text-align: center;
`;

const StyledWhatIsPayByBank = styled(WhatIsPayByBank)`
  color: inherit;
  font: inherit;
  line-height: inherit;
  padding: 0;
`;
